import React from 'react';
import { useHistory } from 'react-router-dom';
import { AvatarSmall } from '../../components';
import css from './EventCard.module.css';
import { formatUsTime, getBrowserTimezone } from '../../util/bookingTime';
import { normalizeCoachifyImageUrl } from '../../util/coachifyPublicImageUrl';
import { categoryToAccentVar } from '../../util/categoryAccent';

const EventCard = ({ event, size = 'default' }) => {
  const history = useHistory();

  const isSmall = size === 'small';

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getEventTypeClass = (type) => {
    const typeMap = {
      Camp: css.badgeCamp,
      Online: css.badgeOnline,
      'In-Person': css.badgeInPerson,
    };
    return typeMap[type] || css.badgeInPerson;
  };

  const getSessionIcon = (type) => {
    if (type === 'Online') {
      return (
        <svg
          className={css.sessionIcon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      );
    }
    if (type === 'Camp') {
      return (
        <svg
          className={css.sessionIcon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M3 17l6-9 4 5 4-5 6 9" />
          <path d="M3 17h18" />
        </svg>
      );
    }
    return (
      <svg
        className={css.sessionIcon}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    );
  };

  const formatTimeValue = (value) => {
    if (!value) return '';
    return formatUsTime(value, getBrowserTimezone()) || String(value);
  };

  const handleClick = () => {
    // Prefer the session type's own id -- every session type a coach
    // offers shares the same Sharetribe shell listingId, so linking by
    // listingId alone can't distinguish one session from another (and that
    // shell listingId no longer resolves at all now that Sharetribe
    // credentials are revoked). FeaturedEvents.jsx (landing page) never
    // sets `sessionTypeId` explicitly -- it passes the raw session object,
    // whose own Mongo _id comes through as `event.id` -- so fall back to
    // that before falling back to the dead listingId. Camps don't have a
    // SessionType detail page, so leave those on the listingId/host-profile
    // fallback below untouched.
    const detailId = event.sessionTypeId || (!event.isCamp && (event.id?.uuid || event.id)) || event.listingId;
    if (detailId) {
      const slug = createSlug(event.title || 'session');
      history.push(`/listing/${slug}/${detailId}`);
    } else {
      const hostId = event.host?.id?.uuid || event.host?.id;
      if (hostId) {
        history.push(`/u/${hostId}`);
      }
    }
  };

  const createSlug = (name) => {
    return name
      ? name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
      : 'session';
  };

  const spotsLeft = event.spotsLeft || 0;
  const isLimited = spotsLeft > 0 && spotsLeft <= 10;
  const hostName = event.host?.displayName || event.author?.displayName || '';
  const displayType = event.sessionType || (event.isCamp ? 'Camp' : 'In-Person');
  const locationLabel = event.location?.address || event.address || '';
  const totalSpots = event.totalSpots || 0;
  const availabilityLabel = totalSpots > 0 ? `${spotsLeft}/${totalSpots} available` : '';
  const startTime = formatTimeValue(event.startTime || event.nextDate) || event.time || '';

  const durationText =
    event.durationLabel || (event.durationHours ? `${event.durationHours} hours` : null) || '';
  const eventCategory = event.mainCategory || '';
  const eventSubcategory = event.subCategory || '';

  const coverSrc = normalizeCoachifyImageUrl(event.image || event.sessionImage);
  const accentVar = categoryToAccentVar(eventCategory);

  return (
    <div className={css.eventCard} onClick={handleClick}>
      <div className={css.topAccent} style={{ background: accentVar }} />
      <div className={css.cardImage}>
        {coverSrc ? (
          <img
            src={coverSrc}
            alt={event.title}
            className={css.cardImg}
          />
        ) : (
          <div className={css.cardImagePlaceholder}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="1.5"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
        )}
        <div className={css.cardBadges}>
          <span className={`${css.badge} ${getEventTypeClass(displayType)}`}>
            {getSessionIcon(displayType)}
            {displayType}
          </span>
        </div>
        {isLimited && <div className={css.spotsLeftBadge}>{spotsLeft} spots left</div>}
        <div className={css.hostAvatar}>
          {(() => {
            const authorImg = event.author?.profileImage;
            const hostImg = event.host?.profileImage;
            const hasAuthorImage = authorImg && (authorImg.id || authorImg.url);
            const hasHostImage = hostImg && (hostImg.id || hostImg.url);
            const imageUrl = hasHostImage
              ? hostImg.url || hostImg.id?.url
              : hasAuthorImage
                ? authorImg.url || authorImg.id?.url
                : null;

            return imageUrl ? (
              <img
                src={imageUrl}
                alt={event.host?.displayName || 'Coach'}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#1a1a1a',
                  color: 'white',
                  fontSize: '18px',
                  fontWeight: 'bold',
                }}
              >
                {hostName.charAt(0)?.toUpperCase()}
              </div>
            );
          })()}
        </div>
      </div>
      <div className={css.cardContent}>
        <div className={css.categoryBadge}>
          <span className={css.categoryBadgeInner}>{eventCategory}</span>
          {eventSubcategory && (
            <>
              <span>/</span>
              <span className={css.categoryText}>{eventSubcategory}</span>
            </>
          )}
        </div>
        <h3 className={css.cardTitle}>{event.title || event.sessionName}</h3>
        <div className={css.hostInfo}>
          <div className={`${css.hostText} ${isSmall ? css.hostTextSmall : ''}`}>
            <span>Hosted by</span>
            <span className={css.hostName}>{hostName}</span>
          </div>
          {event.reviewCount > 0 || event.rating ? (
            <div className={css.ratingRow}>
              <svg className={css.starIcon} viewBox="0 0 24 24" fill="currentColor">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
              </svg>
              <span className={css.ratingValue}>
                {typeof event.rating === 'number' ? event.rating.toFixed(1) : event.rating}
              </span>
              <span className={css.reviewCount}>({event.reviewCount})</span>
            </div>
          ) : (
            <div className={css.ratingRow}>
              <span className={css.reviewCount}>New</span>
            </div>
          )}
        </div>
        <div className={css.metaList}>
          {locationLabel && (
            <div className={`${css.metaItem} ${isSmall ? css.metaItemSmall : ''}`}>
              <svg
                className={css.metaIcon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span className={css.metaText} title={locationLabel}>
                {locationLabel}
              </span>
            </div>
          )}
          {event.nextDate && (
            <div className={`${css.metaItem} ${isSmall ? css.metaItemSmall : ''}`}>
              <svg
                className={css.metaIcon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span>{formatDate(event.nextDate)}</span>
            </div>
          )}
          {(startTime || durationText) && (
            <div className={`${css.metaItem} ${isSmall ? css.metaItemSmall : ''}`}>
              <svg
                className={css.metaIcon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="9" />
                <polyline points="12 7 12 12 16 14" />
              </svg>
              <span
                className={css.metaText}
                title={`${startTime}${startTime && durationText ? ' - ' : ''}${durationText}`}
              >
                {startTime}
                {startTime && durationText ? ' - ' : ''}
                {durationText}
              </span>
            </div>
          )}
        </div>
        <div className={css.cardFooter}>
          <div className={css.spotsInfo}>
            <svg
              className={css.metaIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span>{availabilityLabel}</span>
          </div>
          {event.price > 0 && <div className={css.price}>${event.price}</div>}
        </div>
      </div>
    </div>
  );
};

export default EventCard;
